import * as React from 'react'
import { ActivityIndicator, Text, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import TextInput from '@components/textInput/TextInput'
import Choice from '@components/choice/Choice'
import Animators from '@components/root/Animators'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'
import * as FamilleStore from '@store/famille/famille'

import api from '@api/api'

import { FormikHelpers, useFormik } from 'formik'

import sanitize from '@utils/Sanitizer'
import useBreakpoint from '@utils/useBreakpoints'

import { findFamilyParents } from '../utils'

import { HOTLINE_PHONE } from '@app/config'
import { callPhoneNumber } from '@utils/userUtils'

interface Values {
  libelleFamille?: string
  libelleSousFamille?: string
  libelleArticle?: string
  isCommandeOrganisateur: boolean
  familles: string[]
  famillesMinEditable: number
}

const Command = ({ form, onForm }: IncidentFormProps) => {
  const init: Values = React.useMemo(
    () => ({
      libelleFamille: form.libelleFamille,
      libelleSousFamille: form.libelleSousFamille,
      libelleArticle: form.libelleArticle,
      isCommandeOrganisateur: false,
      familles: form.familles,
      famillesMinEditable: form.famillesMinEditable,
    }),
    [
      form.libelleFamille,
      form.libelleSousFamille,
      form.libelleArticle,
      form.numeroCommande,
      form.familles,
      form.famillesMinEditable,
    ]
  )

  const i18n = useI18n()
  const [, bp] = useBreakpoint()

  const [search, setSearch] = React.useState('')
  const [status, setStatus] = React.useState<'loading' | 'loaded' | 'error'>('loaded')

  const [commandes, setCommandes] = React.useState<CommandeNonConnecteResponse[]>([])

  const familles = useReducer(FamilleStore.store, (s) => s.activities)
  const activities = React.useMemo(() => familles.filter((f) => f.enabled), [familles])

  const filtered = React.useMemo(
    () =>
      commandes
        .filter((c) => c.enseigne === form.enseigneCommande)
        .filter((c) => sanitize(c.libelleArticle).includes(sanitize(search))),
    [commandes, search, form.enseigneCommande]
  )

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setValues, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  const onItemSelected = (id: string) => {
    const item = commandes.find((c) => c.libelleArticle === id)

    if (item) {
      const selectedFamille = activities.find((a) => a.id === item.sousFamilleId)
      const familles = selectedFamille ? findFamilyParents(activities, selectedFamille) : []

      setValues({
        ...values,
        libelleArticle: item.libelleArticle,
        libelleFamille: item.libelleFamille,
        libelleSousFamille: item.libelleSousFamille,
        isCommandeOrganisateur: false,
        familles: familles.map((f) => f.id),
        famillesMinEditable: familles.length,
      })
    }
  }

  React.useEffect(() => {
    if (form.siteId && form.manifestationId) {
      setStatus('loading')

      api.commande
        .expo(form.siteId, form.manifestationId)
        .then((cmds) => setCommandes(cmds.commandes))
        .then(() => setStatus('loaded'))
        .catch(() => setStatus('error'))
    }
  }, [form.siteId, form.manifestationId])

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  if (status === 'error') {
    return (
      <CenteredContainer>
        <Animators>
          <Status>{i18n.t('screens.incident.COMMAND.error')}</Status>
        </Animators>
      </CenteredContainer>
    )
  }

  if (status === 'loading') {
    return (
      <CenteredContainer>
        <Animators>
          <ActivityIndicator color={theme.colors.primary} size="large" />
          <Status>{i18n.t('screens.incident.COMMAND.loading')}</Status>
        </Animators>
      </CenteredContainer>
    )
  }

  const options = filtered.map((b) => ({ label: b.libelleArticle, value: b.libelleArticle }))
  const opt = options.find((o) => values.libelleArticle === o.value)

  return (
    <MainContainer>
      <SearchContainer>
        <TextInput
          value={search}
          onChange={setSearch}
          placeholder={i18n.t('screens.incident.COMMAND.search')}
          left={{ name: 'search' }}
        />
      </SearchContainer>

      <Content>
        <Choice data={options} onItemSelected={(next) => onItemSelected(next.value as string)} value={opt} />
        {bp !== 'phone' && (
          <Complement>
            {i18n.t('screens.incident.COMMAND.infoStart')}
            <PhoneNumber onPress={() => callPhoneNumber(HOTLINE_PHONE)}>{` ${HOTLINE_PHONE} `}</PhoneNumber>
            {i18n.t('screens.incident.COMMAND.infoEnd')}
          </Complement>
        )}
      </Content>
    </MainContainer>
  )
}

export default Command

const MainContainer = styled(View)`
  flex: 1;
`

const CenteredContainer = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
`

const SearchContainer = styled(View)`
  margin: 24px;
`

const Content = styled(View)`
  flex: 1;
  margin: 0 24px;
  margin-bottom: 5px;
`

const Complement = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.tertiary};
  margin: 20px 0 5px;
`

const Status = styled(Text)`
  ${(props) => props.theme.fonts.Option};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
  margin: 15px;
`

const PhoneNumber = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.primary};
`
